/**
 * LoginPage
 *
 * Screens: login | register | disclaimer | pending | reset
 *
 * Critical flow fix: session is NOT set until AFTER disclaimer is accepted.
 * LoginForm returns the pending session data via onSuccess(session).
 * Only handleDisclaimerAccept() commits it via setSession() then navigates.
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, type FomSession } from '../stores/auth'
import LoginForm from '../components/auth/LoginForm'
import RegisterForm from '../components/auth/RegisterForm'
import DisclaimerModal from '../components/auth/DisclaimerModal'
import { sha256, workerPostV1 } from '../api/worker'

type Screen = 'login' | 'register' | 'disclaimer' | 'pending' | 'reset' | 'reset-confirm'

function isMobile() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
}

function Shield() {
  const mobile = isMobile()
  const size = mobile ? 96 : 128
  return (
    <img
      src="./castle-logo.png"
      alt="Castellan"
      width={size}
      height={size}
      style={{ borderRadius: mobile ? 16 : 20, objectFit: 'contain' }}
    />
  )
}

export default function LoginPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore(s => s.setSession)
  const signOut = useAuthStore(s => s.signOut)
  const [screen, setScreen] = useState<Screen>('login')
  const [tenantName, setTenantName] = useState('Castellan Security')
  const [isSubdomain, setIsSubdomain] = useState(false)

  // Tenant name map for known subdomains
  const TENANT_NAMES: Record<string, string> = {
    fom: 'Fair Oaks Mall',
    fom2: 'Fair Oaks Mall',
  }

  // Read tenant from subdomain (primary) or localStorage (fallback)
  useEffect(() => {
    const host = window.location.hostname
    const parts = host.split('.')
    if (parts.length >= 3 && parts[parts.length - 2] === 'castellan-security') {
      const sub = parts[0]
      const mapped = TENANT_NAMES[sub.toLowerCase()]
      setTenantName(mapped ?? (sub.charAt(0).toUpperCase() + sub.slice(1).replace(/-/g, ' ')))
      setIsSubdomain(true)
    } else {
      const stored = localStorage.getItem('castellan_tenant_name')
      if (stored) setTenantName(stored)
    }
  }, [])
  const [pendingSession, setPendingSession] = useState<FomSession | null>(null)
  const [resetCs, setResetCs] = useState('')
  const [resetMsg, setResetMsg] = useState('')
  const [resetLoading, setResetLoading] = useState(false)

  // Reset-confirm screen state
  const [resetCode, setResetCode]               = useState('')
  const [resetNewPw, setResetNewPw]             = useState('')
  const [resetConfirmPw, setResetConfirmPw]     = useState('')
  const [resetConfirmMsg, setResetConfirmMsg]   = useState('')
  const [resetConfirmLoading, setResetConfirmLoading] = useState(false)
  const [resetShowPw, setResetShowPw]           = useState(false)

  // Called by LoginForm BEFORE session is set — holds session in state
  function handleLoginSuccess(session: FomSession) {
    setPendingSession(session)
    setScreen('disclaimer')
  }

  // Called when disclaimer is accepted — NOW we set the session and navigate
  function handleDisclaimerAccept() {
    if (pendingSession) {
      setSession(pendingSession)
    }
    // Redirect back to wherever the user was before session expired
    const intended = sessionStorage.getItem('castellan_intended_route')
    sessionStorage.removeItem('castellan_intended_route')
    if (intended && intended !== '/login') {
      navigate(intended, { replace: true })
    } else {
      navigate(isMobile() ? '/mobile' : '/desktop', { replace: true })
    }
  }

  function handleDisclaimerDecline() {
    setPendingSession(null)
    setScreen('login')
  }

  // RegisterForm success — pending session from registration auto-approval
  function handleRegisterSuccess(session: FomSession) {
    setPendingSession(session)
    setScreen('disclaimer')
  }

  async function handleReset() {
    if (!resetCs.trim()) { setResetMsg('Enter your call sign'); return }
    setResetLoading(true); setResetMsg('')
    try {
      const res = await workerPostV1<{ success: boolean; message?: string }>(
        '/api/v1/auth/reset-request',
        { callSign: resetCs.trim().toUpperCase() },
      )
      if (res.success) {
        // Move to confirm screen so user can paste the code from email
        setResetMsg('')
        setScreen('reset-confirm')
      } else {
        setResetMsg(res.message ?? 'Could not find that call sign.')
      }
    } catch {
      setResetMsg('Network error. Try again.')
    }
    setResetLoading(false)
  }

  async function handleResetConfirm() {
    if (!resetCode.trim())     { setResetConfirmMsg('Enter the 6-digit code from your email'); return }
    if (!resetNewPw)           { setResetConfirmMsg('Enter a new password'); return }
    if (resetNewPw.length < 6) { setResetConfirmMsg('Password must be at least 6 characters'); return }
    if (resetNewPw !== resetConfirmPw) { setResetConfirmMsg('Passwords do not match'); return }
    setResetConfirmLoading(true); setResetConfirmMsg('')
    try {
      const newPasswordHash = await sha256(resetNewPw)
      const res = await workerPostV1<{ success: boolean; message?: string }>(
        '/api/v1/auth/reset-confirm',
        {
          callSign: resetCs.trim().toUpperCase(),
          code: resetCode.trim(),
          newPasswordHash,
        },
      )
      if (res.success) {
        // Clear all reset state and bounce back to login with success banner
        setResetCs('')
        setResetCode('')
        setResetNewPw('')
        setResetConfirmPw('')
        setResetConfirmMsg('')
        setResetMsg('✅ Password reset successful. Sign in with your new password.')
        setScreen('login')
      } else {
        setResetConfirmMsg(res.message ?? 'Could not reset password. Try again.')
      }
    } catch {
      setResetConfirmMsg('Network error. Try again.')
    }
    setResetConfirmLoading(false)
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 16,
    padding: '28px 24px', width: '100%',
    maxWidth: screen === 'register' ? 480 : 360,
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    background: 'var(--bg-deep)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '10px 12px',
    color: 'var(--text-primary)', fontSize: 14, outline: 'none', marginBottom: 10,
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-deep)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 24, gap: 20,
      fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'var(--text-primary)',
    }}>

      {/* Disclaimer renders as fixed overlay — session not yet committed */}
      {screen === 'disclaimer' && (
        <DisclaimerModal
          session={pendingSession}
          onAccept={handleDisclaimerAccept}
          onDecline={handleDisclaimerDecline}
        />
      )}

      <Shield />
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 22, letterSpacing: 2 }}>CASTELLAN</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Security Management Platform</div>
      </div>

      <div style={cardStyle}>

        {screen === 'login' && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 16 }}>
              <strong style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{tenantName}</strong>
              {!isSubdomain && (
                <span
                  onClick={() => navigate('/find-agency')}
                  style={{ color: 'var(--accent)', cursor: 'pointer', marginLeft: 8, fontWeight: 600 }}
                >Switch ›</span>
              )}
            </div>
            {resetMsg && (
              <div style={{
                padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, textAlign: 'center',
                background: 'rgba(34,197,94,0.1)', color: '#22c55e',
                border: '1px solid rgba(34,197,94,0.3)',
              }}>{resetMsg}</div>
            )}
            <LoginForm
              onSuccess={handleLoginSuccess}
              onShowRegister={() => setScreen('register')}
              onShowReset={() => { setResetMsg(''); setScreen('reset') }}
            />
          </>
        )}

        {screen === 'register' && (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent)', marginBottom: 16, textAlign: 'center' }}>
              Create Account
            </div>
            <RegisterForm
              onSuccess={handleRegisterSuccess}
              onPending={() => setScreen('pending')}
              onShowLogin={() => setScreen('login')}
            />
          </>
        )}

        {screen === 'pending' && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--accent)', marginBottom: 10 }}>Account Pending Approval</div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 20 }}>
              Your account has been created and is waiting for a supervisor to approve it.
            </p>
            <button onClick={() => setScreen('login')} style={{
              padding: '10px 24px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer',
            }}>Back to Sign In</button>
          </div>
        )}

        {screen === 'reset' && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, textAlign: 'center' }}>Reset Password</div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, textAlign: 'center' }}>
              Enter your call sign and we'll send a reset code to your registered email.
            </p>
            {resetMsg && (
              <div style={{
                padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, textAlign: 'center',
                background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.3)',
              }}>{resetMsg}</div>
            )}
            <input value={resetCs} onChange={e => setResetCs(e.target.value)} placeholder="Call sign (e.g. S-01)"
              onKeyDown={e => e.key === 'Enter' && handleReset()} style={inputStyle} autoCapitalize="characters" />
            <button onClick={handleReset} disabled={resetLoading} style={{
              width: '100%', padding: 11, background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
              border: 'none', borderRadius: 8, color: '#1a2744', fontWeight: 700,
              fontSize: 14, cursor: resetLoading ? 'not-allowed' : 'pointer',
            }}>
              {resetLoading ? 'Sending…' : 'Send Reset Code'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}>
              <span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => { setScreen('login'); setResetMsg(''); setResetCs('') }}>
                Back to Sign In
              </span>
            </p>
          </div>
        )}

        {screen === 'reset-confirm' && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, textAlign: 'center' }}>Set New Password</div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, textAlign: 'center', lineHeight: 1.5 }}>
              We sent a 6-digit code to the email on file for <strong style={{ color: 'var(--accent)' }}>{resetCs.toUpperCase()}</strong>.
              Enter the code below along with your new password.
            </p>
            {resetConfirmMsg && (
              <div style={{
                padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, textAlign: 'center',
                background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.3)',
              }}>{resetConfirmMsg}</div>
            )}
            <input value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code" inputMode="numeric" maxLength={6}
              style={{ ...inputStyle, letterSpacing: 3, textAlign: 'center', fontWeight: 700, fontSize: 18 }} />
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <input type={resetShowPw ? 'text' : 'password'} value={resetNewPw}
                onChange={e => setResetNewPw(e.target.value)} placeholder="New password (min 6 chars)"
                style={{ ...inputStyle, marginBottom: 0, paddingRight: 40 }} />
              <button type="button" onClick={() => setResetShowPw(v => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>
                {resetShowPw ? '🙈' : '👁️'}
              </button>
            </div>
            <input type={resetShowPw ? 'text' : 'password'} value={resetConfirmPw}
              onChange={e => setResetConfirmPw(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleResetConfirm()}
              placeholder="Confirm new password" style={inputStyle} />
            <button onClick={handleResetConfirm} disabled={resetConfirmLoading} style={{
              width: '100%', padding: 11, background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
              border: 'none', borderRadius: 8, color: '#1a2744', fontWeight: 700,
              fontSize: 14, cursor: resetConfirmLoading ? 'not-allowed' : 'pointer',
              marginTop: 4,
            }}>
              {resetConfirmLoading ? 'Resetting…' : 'Reset Password'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}>
              <span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => { setScreen('reset'); setResetCode(''); setResetNewPw(''); setResetConfirmPw(''); setResetConfirmMsg('') }}>
                Didn't get a code? Resend
              </span>
              {' · '}
              <span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => { setScreen('login'); setResetCs(''); setResetCode(''); setResetNewPw(''); setResetConfirmPw(''); setResetConfirmMsg(''); setResetMsg('') }}>
                Back to Sign In
              </span>
            </p>
          </div>
        )}

      </div>
    </div>
  )
}
